// Waytide-native agent presence on user-facing surfaces. These are
// product staff — they greet, orient, and answer Waytide questions.
// They are NOT part of any user team, can't be given work, and don't
// live in the agent table. Rendered purely as client-side chrome.

import { useState } from "react";
import { X } from "lucide-react";

export type WaytideAgentKey = "vela" | "pearl" | "shore";

interface WaytideAgent {
  key: WaytideAgentKey;
  name: string;
  role: string;
  initial: string;
}

const WAYTIDE_AGENTS: Record<WaytideAgentKey, WaytideAgent> = {
  vela: {
    key: "vela",
    name: "Vela",
    role: "Project Manager, Waytide",
    initial: "V",
  },
  pearl: {
    key: "pearl",
    name: "Pearl",
    role: "Agent Designer, Waytide",
    initial: "P",
  },
  shore: {
    key: "shore",
    name: "Shore",
    role: "Mission Appraiser, Waytide",
    initial: "S",
  },
};

interface WaytideGreeterProps {
  agent: WaytideAgentKey;
  message: React.ReactNode;
  dismissKey?: string;
}

export function WaytideGreeter({
  agent,
  message,
  dismissKey,
}: WaytideGreeterProps) {
  const a = WAYTIDE_AGENTS[agent];
  const storageKey = dismissKey ? `wt_greeter_${dismissKey}_dismissed` : null;
  const [dismissed, setDismissed] = useState(() => {
    if (!storageKey) return false;
    try {
      return localStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  function dismiss() {
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, "1");
      } catch {
        // storage full / private mode — fall through, no persistence
      }
    }
    setDismissed(true);
  }

  return (
    <aside
      className="flex gap-3 rounded-panel border border-edge bg-surface-glass p-4 backdrop-blur"
      data-testid={`waytide-greeter-${a.key}`}
    >
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-accent bg-surface text-sm font-semibold text-accent"
        aria-hidden="true"
      >
        {a.initial}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-sm font-semibold text-primary">{a.name}</p>
          <p className="text-xs text-faint">{a.role}</p>
        </div>
        <div className="text-sm text-muted">{message}</div>
      </div>
      {storageKey && (
        <button
          onClick={dismiss}
          className="cursor-pointer self-start text-faint transition-colors hover:text-primary"
          aria-label={`Dismiss ${a.name}'s message`}
          data-testid={`waytide-greeter-${a.key}-dismiss`}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </aside>
  );
}
